import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { JobWorkSummaryCards } from "@/pages/admin/jobwork/JobWorkSummaryCards";
import { JobWorkFiltersBar } from "@/pages/admin/jobwork/JobWorkFiltersBar";
import { JobsTable } from "@/pages/admin/jobwork/JobsTable";
import { NewJobDialog } from "@/pages/admin/jobwork/NewJobDialog";
import { JobDetailDialog } from "@/pages/admin/jobwork/JobDetailDialog";
import { ReceiveJobDialog } from "@/pages/admin/jobwork/ReceiveJobDialog";
import { QcDialog } from "@/pages/admin/jobwork/QcDialog";
import { ReconcileDialog } from "@/pages/admin/jobwork/ReconcileDialog";
import { CancelJobDialog, DeleteJobDialog } from "@/pages/admin/jobwork/CancelDeleteJobDialogs";
import {
  emptyForm, goldBreakdown, stoneCaratTotal, purityFactor, KARAT_TO_PERMILLE, receiveFineBreakdown,
} from "@/pages/admin/jobwork/jobWorkHelpers";
import { hasAdminSession } from "@/lib/session";

export default function JobWork() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({ karigar_id: "all", status: "all", search: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState({});
  const [qcOpen, setQcOpen] = useState(false);
  const [qcForm, setQcForm] = useState({ qc_result: "pass", remarks: "" });
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileForm, setReconcileForm] = useState({ classification: "approved_wastage", note: "", adjustment_amount: "" });
  const [cancelFor, setCancelFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [staff, setStaff] = useState(null);

  const load = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    const { data } = await api.get("/karigar/jobs", { params });
    setJobs(data);
  };
  const loadSummary = () => api.get("/karigar/dashboard/summary").then(({ data }) => setSummary(data)).catch(() => {});

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/karigar/karigars", { params: { status: "active" } }).then(({ data }) => setKarigars(data));
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {});
    load(); loadSummary();
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const refreshDetail = async (id) => { const { data } = await api.get(`/karigar/jobs/${id}`); setDetail(data); };
  const openDetail = (j) => refreshDetail(j.id);

  const openCreate = () => { setForm(emptyForm()); setDialogOpen(true); };
  const rowKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
  const addDiamond = () => setForm({ ...form, diamonds: [...form.diamonds, { _key: rowKey(), carat: "", pieces: "", certificate_no: "" }] });
  const addStone = () => setForm({ ...form, stones: [...form.stones, { _key: rowKey(), quality: "", carat: "", pieces: "" }] });
  const addMaterial = () => setForm({ ...form, other_materials: [...form.other_materials, { _key: rowKey(), name: "", qty: "", unit: "" }] });

  const fineGoldPreview = goldBreakdown(form.gold.gross_weight_g, form.gold.purity, form.gold.purity_fine_pct, stoneCaratTotal(form.diamonds, form.stones));

  const save = async () => {
    if (!form.karigar_id) { toast.error("Select a karigar"); return; }
    if (!form.product_ref.trim()) { toast.error("Enter a product/design reference"); return; }
    if (form.gold.gross_weight_g && Number(form.gold.gross_weight_g) < 0) { toast.error("Gold Gross Weight cannot be negative"); return; }
    if (form.gold.gross_weight_g && form.gold.purity === "Other" && !Number(form.gold.purity_fine_pct)) { toast.error("Enter a fineness (e.g. 995) for 'Other' purity"); return; }
    try {
      const payload = {
        ...form,
        gold: form.gold.gross_weight_g ? {
          purity: form.gold.purity,
          purity_fine_pct: form.gold.purity === "Other" ? Number(form.gold.purity_fine_pct) || 0 : null,
          gross_weight_g: Number(form.gold.gross_weight_g) || 0,
          net_weight_g: fineGoldPreview.netMetalG,
        } : null,
        diamonds: form.diamonds.map((d) => ({ ...d, carat: Number(d.carat) || 0, pieces: Number(d.pieces) || 0 })),
        stones: form.stones.map((s) => ({ ...s, carat: Number(s.carat) || 0, pieces: Number(s.pieces) || 0 })),
        other_materials: form.other_materials.map((m) => ({ ...m, qty: Number(m.qty) || 0 })),
        quantity: Number(form.quantity) || 1,
        expected_finished_weight_g: Number(form.expected_finished_weight_g) || 0,
        expected_wastage_g: Number(form.expected_wastage_g) || 0,
        making_charges: Number(form.making_charges) || 0,
      };
      await api.post("/karigar/jobs", payload);
      toast.success("Job work order created"); setDialogOpen(false); load(); loadSummary();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save job"); }
  };

  const doIssue = async (job) => {
    try { await api.post(`/karigar/jobs/${job.id}/issue`); toast.success("Material issued to karigar"); load(); loadSummary(); refreshDetail(job.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to issue"); }
  };
  const doStart = async (job) => {
    try { await api.post(`/karigar/jobs/${job.id}/start`); toast.success("Marked In Progress"); load(); refreshDetail(job.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openReceive = (job) => {
    setDetail(job);
    const defaultPreset = job.gold?.purity === "Other" ? "custom" : (KARAT_TO_PERMILLE[job.gold?.purity] || "916");
    setReceiveForm({
      finished_gross_weight_g: "", stone_weight_g: "",
      purity_preset: defaultPreset, purity_fine_pct: defaultPreset === "custom" ? (job.gold?.purity_fine_pct || "") : defaultPreset,
      scrap_fine_g: "",
      actual_making_charges: job.making_charges || "", missing_notes: "", remarks: "",
    });
    setReceiveOpen(true);
  };
  // Fine Gold Issued for this job — shown for reference, never recomputed here.
  const issuedFineForReceive = detail?.gold ? (Number(detail.gold.net_weight_g) || 0) * purityFactor(detail.gold.purity, detail.gold.purity_fine_pct) : 0;
  const receiveBreakdown = receiveFineBreakdown(receiveForm.finished_gross_weight_g, receiveForm.stone_weight_g, receiveForm.purity_fine_pct);
  // Fine Gold Issued − Fine Gold Received − Gold Recovered/Scrap Fine = Fine Gold Difference/Wastage.
  // This is ALWAYS computed — never manually entered. A formal write-off happens later, in Reconcile.
  const receiveDifferencePreview = issuedFineForReceive - receiveBreakdown.fineG - (Number(receiveForm.scrap_fine_g) || 0);
  const onReceivePurityPreset = (v) => setReceiveForm({ ...receiveForm, purity_preset: v, purity_fine_pct: v === "custom" ? "" : v });
  const submitReceive = async () => {
    if (Number(receiveForm.finished_gross_weight_g) < 0) { toast.error("Finished Gross Weight cannot be negative"); return; }
    if (Number(receiveForm.stone_weight_g) < 0) { toast.error("Stone Weight cannot be negative"); return; }
    if (Number(receiveForm.stone_weight_g) > Number(receiveForm.finished_gross_weight_g)) { toast.error("Stone Weight cannot exceed Finished Gross Weight"); return; }
    if (!Number(receiveForm.purity_fine_pct)) { toast.error("Select a Received Purity"); return; }
    try {
      const payload = {
        finished_gross_weight_g: Number(receiveForm.finished_gross_weight_g) || 0,
        stone_weight_g: Number(receiveForm.stone_weight_g) || 0,
        purity: "Other",
        purity_fine_pct: Number(receiveForm.purity_fine_pct) || 0,
        scrap_fine_g: Number(receiveForm.scrap_fine_g) || 0,
        actual_making_charges: Number(receiveForm.actual_making_charges) || 0,
        missing_notes: receiveForm.missing_notes || "",
        remarks: receiveForm.remarks || "",
      };
      await api.post(`/karigar/jobs/${detail.id}/receive`, payload);
      toast.success("Job marked Received"); setReceiveOpen(false); load(); loadSummary(); refreshDetail(detail.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record receipt"); }
  };

  const openQc = (job) => { setDetail(job); setQcForm({ qc_result: "pass", remarks: "" }); setQcOpen(true); };
  const submitQc = async () => {
    try { await api.post(`/karigar/jobs/${detail.id}/qc`, qcForm); toast.success("QC recorded"); setQcOpen(false); load(); refreshDetail(detail.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openReconcile = (job) => { setDetail(job); setReconcileForm({ classification: "approved_wastage", note: "", adjustment_amount: "", approved_loss_recovery_g: (job.reconciliation?.difference_g || 0) }); setReconcileOpen(true); };
  const onReconcileClassification = (v) => {
    // Approved Wastage fully absorbs the physical difference by default -> Fine Balance shows 0.
    // Other classifications default to 0g approved (the gram shortage stays open; only the ₹ side is settled).
    const autoApproved = v === "approved_wastage" ? (detail?.reconciliation?.difference_g || 0) : 0;
    setReconcileForm({ ...reconcileForm, classification: v, approved_loss_recovery_g: autoApproved });
  };
  const reconcileFineBalance = (Number(detail?.reconciliation?.difference_g) || 0) - (Number(reconcileForm.approved_loss_recovery_g) || 0);
  const submitReconcile = async () => {
    try {
      await api.post(`/karigar/jobs/${detail.id}/reconcile`, {
        ...reconcileForm,
        adjustment_amount: Number(reconcileForm.adjustment_amount) || 0,
        approved_loss_recovery_g: Number(reconcileForm.approved_loss_recovery_g) || 0,
      });
      toast.success("Reconciliation classified"); setReconcileOpen(false); load(); refreshDetail(detail.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to reconcile"); }
  };

  const doComplete = async (job) => {
    try { await api.post(`/karigar/jobs/${job.id}/complete`); toast.success("Job completed"); load(); loadSummary(); refreshDetail(job.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to complete — classify the reconciliation first"); }
  };
  const doCancel = async () => {
    try { await api.post(`/karigar/jobs/${cancelFor.id}/cancel`); toast.success("Job cancelled"); setCancelFor(null); load(); loadSummary(); }
    catch (err) { toast.error(err.response?.data?.detail || "Cancel failed"); }
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    try { await api.delete(`/karigar/jobs/${deleteFor.id}`); toast.success("Job deleted"); setDeleteFor(null); setDetail(null); load(); loadSummary(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete job"); }
  };

  const diff = detail?.reconciliation?.difference_g;
  const detailShown = !receiveOpen && !qcOpen && !reconcileOpen ? detail : null;

  return (
    <AdminShell title="Job Work">
      <JobWorkSummaryCards summary={summary} />

      <JobWorkFiltersBar filters={filters} setFilters={setFilters} karigars={karigars} onNewJob={openCreate} />

      <JobsTable jobs={jobs} onOpenDetail={openDetail} />

      <NewJobDialog
        open={dialogOpen} onClose={() => setDialogOpen(false)} form={form} setForm={setForm}
        karigars={karigars} products={products} fineGoldPreview={fineGoldPreview}
        onAddDiamond={addDiamond} onAddStone={addStone} onAddMaterial={addMaterial} onSave={save}
      />

      <JobDetailDialog
        detail={detailShown} onClose={() => setDetail(null)} staff={staff} diff={diff}
        onIssue={doIssue} onStart={doStart} onOpenReceive={openReceive} onOpenQc={openQc}
        onOpenReconcile={openReconcile} onComplete={doComplete} onCancel={setCancelFor} onDelete={setDeleteFor}
      />

      <ReceiveJobDialog
        open={receiveOpen} onClose={() => setReceiveOpen(false)} jobNo={detail?.job_no}
        receiveForm={receiveForm} setReceiveForm={setReceiveForm} onPurityPreset={onReceivePurityPreset}
        receiveBreakdown={receiveBreakdown} issuedFineForReceive={issuedFineForReceive}
        differencePreview={receiveDifferencePreview} onSubmit={submitReceive}
      />

      <QcDialog open={qcOpen} onClose={() => setQcOpen(false)} jobNo={detail?.job_no} qcForm={qcForm} setQcForm={setQcForm} onSubmit={submitQc} />

      <ReconcileDialog
        open={reconcileOpen} onClose={() => setReconcileOpen(false)} detail={detail}
        reconcileForm={reconcileForm} setReconcileForm={setReconcileForm}
        onClassificationChange={onReconcileClassification} fineBalance={reconcileFineBalance} onSubmit={submitReconcile}
      />

      <CancelJobDialog cancelFor={cancelFor} onClose={() => setCancelFor(null)} onConfirm={doCancel} />

      <DeleteJobDialog deleteFor={deleteFor} onClose={() => setDeleteFor(null)} onConfirm={doDelete} />
    </AdminShell>
  );
}
